/* eslint-disable perfectionist/sort-objects */
import { theme } from '@braskai/ui-kit';

import { USER_PROMPT_FAQ_LINK } from '@/features/project/user-prompting';

import AIScriptImg from '@/shared/assets/images/pricing/featureTooltips/ai-script.jpg';
import APIImg from '@/shared/assets/images/pricing/featureTooltips/api.jpg';
import ApplyChangesForFreeImg from '@/shared/assets/images/pricing/featureTooltips/apply-changes-for-free.jpg';
import CaptionImg from '@/shared/assets/images/pricing/featureTooltips/captions.jpg';
import CustomVoiceCloneImg from '@/shared/assets/images/pricing/featureTooltips/custom-voice-clone.jpg';
import CustomVoiceSettingsImg from '@/shared/assets/images/pricing/featureTooltips/custom-voice-settings.jpg';
import DictionaryImg from '@/shared/assets/images/pricing/featureTooltips/dictionary.jpg';
import HumanInTheLoopImg from '@/shared/assets/images/pricing/featureTooltips/human-in-the-loop.jpg';
import LanguagesImg from '@/shared/assets/images/pricing/featureTooltips/languages.jpg';
import LipSyncImg from '@/shared/assets/images/pricing/featureTooltips/lip-sync.jpg';
import ExtendedMinutesImg from '@/shared/assets/images/pricing/featureTooltips/minutes-extended.jpg';
import MinutesImg from '@/shared/assets/images/pricing/featureTooltips/minutes.jpg';
import SimultaneousMultiLanguageImg from '@/shared/assets/images/pricing/featureTooltips/simultaneous-multi-language.jpg';
import SRTDownloadImg from '@/shared/assets/images/pricing/featureTooltips/srt-download.jpg';
import SRTUploadImg from '@/shared/assets/images/pricing/featureTooltips/srt-upload.jpg';
import TeamspacesImg from '@/shared/assets/images/pricing/featureTooltips/teamspaces.jpg';
import UserPromptingImg from '@/shared/assets/images/pricing/featureTooltips/translation-prompting.png';
import VideoEditorImg from '@/shared/assets/images/pricing/featureTooltips/video-editor.jpg';
import VoiceCloneImg from '@/shared/assets/images/pricing/featureTooltips/voice-clone.jpg';
import {
  AI_REWRITER_DOC_LINK,
  API_DOCUMENTATION_LINK,
  LANGUAGES_DOC_LINK,
  LIP_SYNC_DOC_LINK,
  MULTILANGUAGE_DOC_LINK,
  SRT_DOWNLOAD_DOC_LINK,
  SRT_UPLOAD_DOC_LINK,
  TEAMSPACES_DOC_LINK,
  VIDEO_CAPTIONS_DOC_LINK,
  VOICE_CLONING_DOC_LINK,
} from '@/shared/constants/docLinks';
import { inIframe } from '@/shared/lib/browser';
import { Plan, PlanCategory } from '@/shared/types/Billing';

import { TFeature } from './types';

export const CATEGORY_ORDER: Record<PlanCategory, number> = {
  business: 4,
  creator: 2,
  creator_pro: 3,
  enterprise: 5,
  starter: 1,
};

export const categoryWithSlider = new Set([PlanCategory.Business, PlanCategory.Creator, PlanCategory.CreatorPro]);

const baseMinutesText = 'Use minutes for Translation';
const advancedMinutesText = 'Use minutes for Translation and Lip-sync';

export const MINUTES_TEXT = {
  business: advancedMinutesText,
  creator: baseMinutesText,
  creator_pro: advancedMinutesText,
  enterprise: advancedMinutesText,
  starter: null,
} as const;

const BASE_TOOLTIP_MINUTES_INFO = {
  title: 'What does minute mean?',
  description:
    '1 minute = 1 minute of translated content. For example, to translate a 1-minute video into two languages, you will need 2 minutes.',
  image: MinutesImg,
};

const EXTENDED_TOOLTIP_MINUTES_INFO = {
  ...BASE_TOOLTIP_MINUTES_INFO,
  description:
    '1 minute = 1 minute of translated content or lip-sync. For example, to translate a 1-minute video into two languages, you will need 2 minutes. To apply lip-sync to these videos, you need 2 more minutes.',
  image: ExtendedMinutesImg,
};

export const TOOLTIP_MINUTES_INFO = {
  business: EXTENDED_TOOLTIP_MINUTES_INFO,
  creator: BASE_TOOLTIP_MINUTES_INFO,
  creator_pro: EXTENDED_TOOLTIP_MINUTES_INFO,
  enterprise: EXTENDED_TOOLTIP_MINUTES_INFO,
  starter: null,
} as const;

export const FEATURE_BLOCKS_KEYS = {
  aiVideoGeneration: 'aiVideoGeneration',
  translation: 'translation',
} as const;

export const ENTERPRISE_BACKGROUND_COLOR = '#1E1E70'; //color used only in pricing page
export const BUSINESS_BACKGROUND_COLOR = '#E7E7FC'; //color used only in pricing page

// Brand blue (Figma: base/brand) — active billing toggle + selected mobile plan tab.
export const BRAND_BLUE = '#0000ff';

export const PRICING_PLAN_CARD_NOTES = {
  [PlanCategory.Business]: {
    label: 'Best Value',
    backgroundColor: BUSINESS_BACKGROUND_COLOR,
    color: 'grey.700',
  },
  [PlanCategory.Creator]: {
    label: 'Best for individuals',
    backgroundColor: theme.palette.common.white,
    color: 'grey.500',
  },
  [PlanCategory.CreatorPro]: {
    label: 'Most Popular',
    backgroundColor: theme.palette.primary[600],
    color: 'common.white',
  },
  [PlanCategory.Enterprise]: {
    label: 'Tailored onboarding & SLA',
    backgroundColor: ENTERPRISE_BACKGROUND_COLOR,
    color: 'common.white',
  },
  [PlanCategory.Starter]: {
    label: 'Best for beginners',
    backgroundColor: theme.palette.common.white,
    color: 'grey.500',
  },
} as const;

export const FEATURES_KEYS = {
  aiRewriter: 'aiRewriter',
  allBusiness: 'allBusiness',
  allCreator: 'allCreator',
  allCreatorPro: 'allCreatorPro',
  api: 'api',
  applyChangesForFree: 'applyChangesForFree',
  businessContract: 'businessContract',
  customerSuccessManager: 'customerSuccessManager',
  customVoiceClone: 'customVoiceClone',
  customVoiceCloneBusiness: 'customVoiceCloneBusiness',
  customVoiceCloneEnterprise: 'customVoiceCloneEnterprise',
  customVoiceClonePro: 'customVoiceClonePro',
  customVoiceSettings: 'customVoiceSettings',
  flexibleMinutes: 'flexibleMinutes',
  humanInTheLoop: 'humanInTheLoop',
  lipSync: 'lipSync',
  machineTranslation: 'machineTranslation',
  multiLanguage: 'multiLanguage',
  noWatermark: 'noWatermark',
  priorityProcessing: 'priorityProcessing',
  speechToText: 'speechToText',
  srtDownload: 'srtDownload',
  srtDownloadUpload: 'srtDownloadUpload',
  srtUpload: 'srtUpload',
  support247: 'support247',
  teamspaces: 'teamspaces',
  teamspacesBusiness: 'teamspacesBusiness',
  teamspacesCreatorPro: 'teamspacesCreatorPro',
  teamspacesEnterprise: 'teamspacesEnterprise',
  translate: 'translate',
  translationDictionary: 'translationDictionary',
  userPrompting: 'userPrompting',
  videoCaptions: 'videoCaptions',
  videoEditor: 'videoEditor',
  voiceCloning: 'voiceCloning',
  watermark: 'watermark',
  onlyVideoDownload: 'onlyVideoDownload',
} as const;

export const FEATURES_MAP: Record<keyof typeof FEATURES_KEYS, TFeature> = {
  [FEATURES_KEYS.aiRewriter]: {
    description: 'AI script adjustment',
    tooltip: {
      title: 'Improve translated transcript with AI-driven alignment for optimal speech synchronisation',
      image: AIScriptImg,
      link: AI_REWRITER_DOC_LINK,
    },
  },
  [FEATURES_KEYS.allBusiness]: {
    description: 'All Business features, plus:',
  },
  [FEATURES_KEYS.allCreator]: {
    description: 'All Creator features, plus:',
  },
  [FEATURES_KEYS.allCreatorPro]: {
    description: 'All Creator Pro features, plus:',
  },
  [FEATURES_KEYS.api]: {
    description: 'API',
    tooltip: {
      title: 'Seamlessly integrate Rask with your existing platforms and workflows',
      image: APIImg,
      link: API_DOCUMENTATION_LINK,
      linkTitle: 'Read documentation',
    },
  },
  [FEATURES_KEYS.applyChangesForFree]: {
    description: 'Apply changes for free',
    tooltip: {
      title: 'Change transcript, timestamps and voice settings free of charge',
      image: ApplyChangesForFreeImg,
    },
  },
  [FEATURES_KEYS.businessContract]: {
    description: 'Business contract and invoicing',
  },
  [FEATURES_KEYS.customerSuccessManager]: {
    description: 'Customer Success Manager',
  },
  [FEATURES_KEYS.customVoiceClone]: {
    description: 'Voice Clone library',
    tooltip: {
      title: 'Achieve consistently high-quality cloned audio with the Custom Voice Clone feature.',
      image: CustomVoiceCloneImg,
    },
  },
  [FEATURES_KEYS.customVoiceCloneBusiness]: {
    description: '10 custom voice clones',
    tooltip: {
      title: 'Achieve consistently high-quality cloned audio with the Custom Voice Clone feature.',
      description:
        'Save your voice in a library and use it across projects. Create different voices to match the mood.',
      image: CustomVoiceCloneImg,
    },
  },
  [FEATURES_KEYS.customVoiceCloneEnterprise]: {
    description: 'Custom',
    tooltip: {
      title: 'Achieve consistently high-quality cloned audio with the Custom Voice Clone feature.',
      description:
        'Save your voice in a library and use it across projects. Create different voices to match the mood.',
      image: CustomVoiceCloneImg,
    },
  },
  [FEATURES_KEYS.customVoiceClonePro]: {
    description: '3 custom voice clones',
    tooltip: {
      title: 'Achieve consistently high-quality cloned audio with the Custom Voice Clone feature.',
      description:
        'Save your voice in a library and use it across projects. Create different voices to match the mood.',
      image: CustomVoiceCloneImg,
    },
  },
  [FEATURES_KEYS.customVoiceSettings]: {
    description: 'Voice Clone settings',
    tooltip: {
      title: 'Adjust the level of similarity and expressiveness of your voice clone.',
      description: 'Create diverse voice tones to have full control over the result.',
      image: CustomVoiceSettingsImg,
    },
  },
  [FEATURES_KEYS.flexibleMinutes]: {
    description: 'Flexible minutes pricing',
  },
  [FEATURES_KEYS.humanInTheLoop]: {
    description: 'Human-in-the-loop',
    tooltip: {
      title: 'Enhance AI dubbing results',
      description: 'Request human proofreading for translated speech to create top-quality content',
      image: HumanInTheLoopImg,
    },
  },
  [FEATURES_KEYS.lipSync]: {
    description: 'Multispeaker Lip-sync',
    tooltip: {
      title: 'Get natural and top-quality dubbing by syncing mouth movements with translated audio.',
      description:
        'It takes 1 extra minute to apply lip-sync to 1 minute of translated video. You can try 1 free minute to test the quality.',
      image: LipSyncImg,
      link: LIP_SYNC_DOC_LINK,
    },
  },
  [FEATURES_KEYS.machineTranslation]: {
    description: 'Automated machine translation',
  },
  [FEATURES_KEYS.multiLanguage]: {
    description: 'Simultaneous multi-language translation',
    tooltip: {
      title: 'Create multi-language projects and instantly translate your content into several languages at once',
      image: SimultaneousMultiLanguageImg,
      link: MULTILANGUAGE_DOC_LINK,
    },
  },

  [FEATURES_KEYS.noWatermark]: {
    description: 'No Watermark',
  },
  [FEATURES_KEYS.onlyVideoDownload]: {
    description: 'Only video download',
  },
  [FEATURES_KEYS.priorityProcessing]: {
    description: 'Priority processing',
  },
  [FEATURES_KEYS.speechToText]: {
    description: 'Auto speech-to-text transcription',
  },
  [FEATURES_KEYS.srtDownload]: {
    description: 'SRT download',
    tooltip: {
      title: 'Download .SRT subtitle file containing both translated and original video transcript',
      image: SRTDownloadImg,
      link: SRT_DOWNLOAD_DOC_LINK,
    },
  },
  [FEATURES_KEYS.srtDownloadUpload]: {
    description: 'SRT download & upload',
    tooltip: {
      title: 'Download subtitles with original and translated text, or upload your own to sync with dubbing.',
      image: SRTDownloadImg,
      link: SRT_DOWNLOAD_DOC_LINK,
    },
  },
  [FEATURES_KEYS.srtUpload]: {
    description: 'SRT upload',
    tooltip: {
      title: 'Upload your own video transcript and effortlessly pair it with video for accurate dubbing results',
      image: SRTUploadImg,
      link: SRT_UPLOAD_DOC_LINK,
    },
  },
  [FEATURES_KEYS.support247]: {
    description: 'Support 24/7',
  },
  [FEATURES_KEYS.teamspaces]: {
    description: 'Teamspaces',
  },
  [FEATURES_KEYS.teamspacesBusiness]: {
    description: '1 Teamspace, 5 members',
    tooltip: {
      title: 'Collaborate with your team with Teamspaces feature.',
      image: TeamspacesImg,
      link: TEAMSPACES_DOC_LINK,
    },
  },
  [FEATURES_KEYS.teamspacesCreatorPro]: {
    description: '1 Teamspace, 3 members',
    tooltip: {
      title: 'Collaborate with your team with Teamspaces feature.',
      image: TeamspacesImg,
      link: TEAMSPACES_DOC_LINK,
    },
  },
  [FEATURES_KEYS.teamspacesEnterprise]: {
    description: 'Custom',
    tooltip: {
      title: 'Collaborate with your team with Teamspaces feature.',
      image: TeamspacesImg,
      link: TEAMSPACES_DOC_LINK,
    },
  },
  [FEATURES_KEYS.translate]: {
    description: 'Translations in 135 languages',
    tooltip: {
      title: 'Translate your content into 135 languages and dialects',
      description: 'Depends on the language you can use AI voices or Voice Clone feature',
      image: LanguagesImg,
      link: LANGUAGES_DOC_LINK,
    },
  },
  [FEATURES_KEYS.translationDictionary]: {
    description: 'Translation dictionary',
    tooltip: {
      title: 'Save your custom terminology to ensure consistent automatic translation across all projects',
      image: DictionaryImg,
    },
  },
  [FEATURES_KEYS.userPrompting]: {
    description: 'Translation prompting',
    tooltip: {
      description:
        'Set the tone, style, and context for translations using prompts. Use our templates or create your own to match your specific needs.',
      image: UserPromptingImg,
      link: USER_PROMPT_FAQ_LINK,
    },
  },
  [FEATURES_KEYS.videoCaptions]: {
    description: 'Automated captions/subtitles',
    tooltip: {
      title: 'Generate subtitles and embed them into your translated video',
      image: CaptionImg,
      link: VIDEO_CAPTIONS_DOC_LINK,
    },
  },
  [FEATURES_KEYS.videoEditor]: {
    description: 'Advanced video editor',
    tooltip: {
      title:
        'Easily edit AI-generated content for translation, transcription, timestamps, speakers, and voices to achieve top-quality dubbing results!',
      image: VideoEditorImg,
    },
  },
  [FEATURES_KEYS.voiceCloning]: {
    description: 'Voice Cloning in 32 languages',
    tooltip: {
      title: 'Voice Cloning in 32 languages',
      description:
        'English, Japanese, Chinese, German, Hindi, French, Korean, Portuguese, Italian, Spanish, Indonesian, Dutch, Turkish, Filipino, Polish, Swedish, Bulgarian, Romanian, Arabic, Czech, Greek, Finnish, Croatian, Malay, Slovak, Danish, Tamil, Ukrainian, Russian, Hungarian, Norwegian, Vietnamese.',
      image: VoiceCloneImg,
      link: VOICE_CLONING_DOC_LINK,
    },
  },
  [FEATURES_KEYS.watermark]: {
    description: 'Watermark',
  },
};

// Translation features list
const starterTranslationFeaturesList = [
  FEATURES_KEYS.translate,
  FEATURES_KEYS.machineTranslation,
  FEATURES_KEYS.speechToText,
  FEATURES_KEYS.voiceCloning,
];
const creatorTranslationFeaturesList = [
  FEATURES_KEYS.translate,
  FEATURES_KEYS.speechToText,
  FEATURES_KEYS.machineTranslation,
  FEATURES_KEYS.voiceCloning,
  FEATURES_KEYS.videoEditor,
  FEATURES_KEYS.applyChangesForFree,
  ...(inIframe() ? [] : [FEATURES_KEYS.api]),
  FEATURES_KEYS.noWatermark,
];

const creatorProTranslationFeaturesList = [
  FEATURES_KEYS.allCreator,
  FEATURES_KEYS.lipSync,
  FEATURES_KEYS.srtDownload,
  FEATURES_KEYS.srtUpload,
  FEATURES_KEYS.aiRewriter,
  FEATURES_KEYS.videoCaptions,
  FEATURES_KEYS.customVoiceSettings,
  FEATURES_KEYS.customVoiceClonePro,
  FEATURES_KEYS.teamspacesCreatorPro,
];

const businessTranslationFeaturesList = [
  FEATURES_KEYS.allCreatorPro,
  FEATURES_KEYS.multiLanguage,
  FEATURES_KEYS.translationDictionary,
  FEATURES_KEYS.customVoiceCloneBusiness,
  FEATURES_KEYS.teamspacesBusiness,
];

const enterpriseTranslationFeaturesList = [
  FEATURES_KEYS.allBusiness,
  FEATURES_KEYS.humanInTheLoop,
  FEATURES_KEYS.customerSuccessManager,
  FEATURES_KEYS.flexibleMinutes,
  FEATURES_KEYS.businessContract,
  FEATURES_KEYS.customVoiceCloneEnterprise,
  FEATURES_KEYS.teamspacesEnterprise,
  FEATURES_KEYS.userPrompting,
];

export const featuresByCategory = {
  [PlanCategory.Business]: businessTranslationFeaturesList,
  [PlanCategory.Creator]: creatorTranslationFeaturesList,
  [PlanCategory.CreatorPro]: creatorProTranslationFeaturesList,
  [PlanCategory.Enterprise]: enterpriseTranslationFeaturesList,
  [PlanCategory.Starter]: starterTranslationFeaturesList,
};

export const CUSTOM_PLAN_DATA: Plan = {
  id: 'enterprise',
  name: 'Enterprise',
  bundles: null,
  category: PlanCategory.Enterprise,
  description: 'For enterprises seeking tailored service',
  is_archived: false,
  metadata: {
    label: null,
    bundle_position: 1,
    bundle_value: 'From 2000 min/month',
    bundle_value_default: false,
    extra_minutes_price: 'Custom price',
    highlighted_button: null,
    position: 4,
  },
  minutes_to_add: null,
  prices: [
    { id: 'price_1NC0kzEQhzwkdhyH81hM4KtE', currency: 'usd', recurring: null, status: 'active', unit_amount: 0 },
  ],
};
export const CUSTOM_PLAN_DATA_NEW = {
  name: 'Enterprise',
  category: PlanCategory.Enterprise,
  description: 'For enterprises seeking tailored service',
  plans: [CUSTOM_PLAN_DATA],
};
